import React, { useEffect, useState } from "react";
import { useGetBillingDetailsAndConfigByClientIdMutation } from "../../../api/services/client/clientApis";
import { BillingDetailsAndConfigurationResponse } from "../../../api/services/client/models";
import {
  AccordionHeader,
  AccordionHeaderText,
  ITabs,
  MuiTabPanel,
  MuiTabs,
} from "../../../components";
import { PAGE_IDS } from "../../../helpers/constants";
import { isEmpty, isNotEmpty } from "../../../helpers/utils";
import { useSiteCore } from "../../../hooks/useSiteCore";
import { PurchaseOrderList } from "../../createClient/components/purchaseOrderList/PurchaseOrderList";
import { billingAndPurchaseOrderKeys } from "../../createClient/siteCore/SiteCoreKeys";
import { ViewBillingAndPurchaseOrderWrapper } from "./viewBillingAndPurchaseOrder.style";
import ViewBillingConfigurations from "./viewBillingConfigurations/ViewBillingConfigurations";
import ViewBillingDetails from "./viewBillingDetails/ViewBillingDetails";

interface ViewBillingAndPurchaseOrderProps {
  clientId: string;
  parentAccordionStatus: boolean;
  onAccordionChange: (event: React.SyntheticEvent, expanded: boolean) => void;
}

const pageId = PAGE_IDS.BILLING_AND_PURCHASE_ORDER;
const ViewBillingAndPurchaseOrder: React.FC<
  ViewBillingAndPurchaseOrderProps
> = ({ parentAccordionStatus, clientId, onAccordionChange }) => {
  const [data, setData] = useState<BillingDetailsAndConfigurationResponse>();

  const [selectedTab, setSelectedTab] = useState(0);
  const { fetchLabel, fetchComponentTitle } = useSiteCore();

  const {
    mutateAsync: getBillingDetailsAndConfigByClientId,
    data: clientBillingData,
    isSuccess: isBillingDetailsGetApiSuccess,
  } = useGetBillingDetailsAndConfigByClientIdMutation();

  const {
    billingDetailsAndPOTitle,
    purchaseOrderTrackingTab,
    billingDetailsAndConfigurationTab,
  } = billingAndPurchaseOrderKeys;

  useEffect(() => {
    if (isEmpty(clientId)) return;
    getBillingDetailsAndConfigByClientId(clientId);
  }, [clientId]);

  useEffect(() => {
    if (clientBillingData)
      setData(clientBillingData.data as BillingDetailsAndConfigurationResponse);
  }, [isBillingDetailsGetApiSuccess]);

  const tabIdBilling = `${pageId}-tab1`;
  const tabIdPurchaseOrder = `${pageId}-tab2`;
  const tabs: ITabs[] = [
    {
      id: tabIdBilling,
      label: fetchLabel(
        billingDetailsAndPOTitle,
        billingDetailsAndConfigurationTab,
      ),
    },
    {
      id: tabIdPurchaseOrder,
      label: fetchLabel(billingDetailsAndPOTitle, purchaseOrderTrackingTab),
    },
  ];

  const onTabChange = (event: React.ChangeEvent<{}>, value: number) => {
    setSelectedTab(value);
  };

  return (
    <ViewBillingAndPurchaseOrderWrapper>
      <AccordionHeader
        title={
          <AccordionHeaderText
            label={fetchComponentTitle(billingDetailsAndPOTitle)}
          />
        }
        onChange={onAccordionChange}
        isExpanded={parentAccordionStatus}
      >
        <MuiTabs
          id={pageId}
          tabs={tabs}
          value={selectedTab}
          onChange={onTabChange}
          tabsTestIds={[tabIdBilling, tabIdPurchaseOrder]}
        >
          <MuiTabPanel
            panelId={`${pageId}-panel1`}
            value={selectedTab}
            index={0}
            ariaLabelledById={tabIdBilling}
          >
            {data && <ViewBillingDetails data={data} />}
            {data &&
              data.billingDetails &&
              isNotEmpty(data.billingDetails.billingConfigurations) && (
                <ViewBillingConfigurations
                  clientId={clientId}
                  data={data.billingDetails.billingConfigurations}
                />
              )}
          </MuiTabPanel>

          <MuiTabPanel
            panelId={`${pageId}-panel2`}
            value={selectedTab}
            index={1}
            ariaLabelledById={tabIdPurchaseOrder}
          >
            <PurchaseOrderList isAddButtonVisible={false} />
          </MuiTabPanel>
        </MuiTabs>
      </AccordionHeader>
    </ViewBillingAndPurchaseOrderWrapper>
  );
};

export default ViewBillingAndPurchaseOrder;
